import React, {Fragment, useContext, useEffect, useMemo, useState} from "react";
import {
    UserHomepageComponentExpandability,
    UserHomepageComponentTypes,
    UserHomepageControllerEvents,
    UserHomepageEventEmitter,
    UserHomepageViewMode
} from "../../../../models";
import UserWebHomepageSlideShow from "./types/slideshow";
import UserWebHomepageCategories from "./types/categories";
import UserWebHomepageBanner from "./types/banner";
import UserWebHomepageTimeBased from "./types/time-based";
import UserWebHomepageProducts from "./types/products";
import UserWebHomepageImageCarousel from "./types/image-carousel";
import {createUUId} from "../../../../../../../core/services/utils/utils";
import {UserHomepageControllerContext, UserHomepageViewContext} from "../../../../index";
import classnames from "classnames";
import {Tooltip} from "@mui/material";
import useIsMounted from "../../../../../../hooks/use-is-mounted";
import DateUtils from "../../../../../../../core/services/date-utils";
import UserHomepageUtils from "../../../../core/utils";

const UserWebHomepageComponent = ({component, tabId, bodyId, viewMode, timeBased = false}) => {
    const view = useContext(UserHomepageViewContext);
    const controller = useContext(UserHomepageControllerContext);
    const [ticker, setTicker] = useState(false);
    const isMounted = useIsMounted();

    const notVisible = useMemo(() => {
        if (viewMode === UserHomepageViewMode.preview) {
            return false;
        }
        if ([UserHomepageComponentTypes.timeBased].includes(component.type.id)
            && !UserHomepageUtils.getTimeBasedFinishDateTime(component)) {
            // time based. so if no finish date, it should not be visible
            return true;
        }
        return (!!component.endDate && DateUtils.serverNow().isAfter(DateUtils.server(component.endDate))) ||
            (!!component.startDate && DateUtils.serverNow().isBefore(DateUtils.server(component.startDate)));
    }, [component, viewMode, ticker])


    /**
     * As soon as the component mounts:
     * - sets an interval that would re-render the component each second to ensure that the visibility of this
     * component is updated if at any time the component expires.
     */
    useEffect(() => {
        const callback = () => {
            if (!isMounted())
                return;
            setTicker(prevState => !prevState);
            timer = setTimeout(callback, 1000)
        }
        let timer = setTimeout(callback, 1000);
        return () => clearTimeout(timer);
    }, [])

    /**
     * With each change in the controller:
     * - attaches the event listeners for component and content selection for focusing purposes.
     */
    useEffect(() => {
        controller.on(UserHomepageControllerEvents.onComponentSelected, focusComponent)
        return () => {
            controller.off(UserHomepageControllerEvents.onComponentSelected, focusComponent)
        }
    }, [controller, component, view, bodyId])

    /**
     * Scrolls the component into the view as a result of control panel selection of the component
     * @param {UserHomepageEventEmitter} emitter
     * @param {number} tabId
     * @param {number} componentId
     */
    const focusComponent = (emitter, tabId, componentId) => {
        if (componentId !== component.id)
            return;
        const body = document.getElementById(bodyId);
        body.scrollTo({
            top: document.getElementById(component.webViewElementId)?.offsetTop - body.offsetTop,
            left: 0,
        })
    }

    /**
     * Invokes the appropriate controller callback for the selection of the component.
     * */
    const componentSelected = () => {
        let expandedMode;
        if (!notVisible || [UserHomepageComponentTypes.timeBased].includes(component.type.id)) {
            expandedMode = UserHomepageComponentExpandability.content;
        } else {
            expandedMode = UserHomepageComponentExpandability.dates;
        }
        controller.selectComponent(
            tabId,
            component.id,
            UserHomepageEventEmitter.view,
            expandedMode
        );
    }

    /**
     * Redness the content of this component based on their type.
     * @type {JSX.Element}
     */
    const content = useMemo(() => {
        switch (component.type?.id) {
            case UserHomepageComponentTypes.slideShow:
                return <UserWebHomepageSlideShow
                    key={component.id}
                    component={component}
                    tabId={tabId}
                />;
            case UserHomepageComponentTypes.category:
                return <UserWebHomepageCategories
                    key={component.id}
                    component={component}
                    tabId={tabId}
                    viewMode={viewMode}
                />;
            case UserHomepageComponentTypes.banner:
                return <UserWebHomepageBanner
                    key={component.id}
                    component={component}
                    tabId={tabId}
                />;
            case UserHomepageComponentTypes.timeBased:
                return <UserWebHomepageTimeBased
                    key={component.id}
                    component={component}
                    tabId={tabId}
                    bodyId={bodyId}
                />;
            case UserHomepageComponentTypes.smallProduct:
            case UserHomepageComponentTypes.mediumProduct:
            case UserHomepageComponentTypes.largeProduct:
                return <UserWebHomepageProducts
                    key={component.id}
                    component={component}
                    tabId={tabId}
                    hideHeader={timeBased}
                />;
            case UserHomepageComponentTypes.imageCarousel:
                return <UserWebHomepageImageCarousel
                    key={component.id}
                    component={component}
                    tabId={tabId}
                />;
            default:
                return <Fragment key={createUUId(true)}/>;
        }
    }, [component, tabId, timeBased, viewMode])

    const isBleedComponent = useMemo(() => [
        UserHomepageComponentTypes.slideShow,
        UserHomepageComponentTypes.banner,
    ].includes(component.type?.id), [component.type?.id]);

    /**
     * The render-able content of this component
     * @type {JSX.Element}
     */
    const children = useMemo(() => {
        return (
            <div
                onClick={componentSelected}
                id={component.webViewElementId}
                className={classnames(
                    'user-web-homepage-component',
                    {
                        'user-web-homepage-section': component.type?.id !== UserHomepageComponentTypes.timeBased && !timeBased,
                        'user-web-homepage-section--bleed': isBleedComponent,
                        [view.viewportWidth]: component.type?.id !== UserHomepageComponentTypes.timeBased,
                        'not-visible': notVisible,
                    }
                )}
            >
                {content}
            </div>
        );
    }, [component, content, notVisible, view.viewportWidth, timeBased, isBleedComponent])

    return (
        <>
            {
                notVisible
                    ? <Tooltip
                        title={'This component is not visible to the users due to the specified date range.'}
                        placement={'top'}
                    >
                        {children}
                    </Tooltip>
                    : children
            }
        </>
    )
}

export default UserWebHomepageComponent;
